import * as THREE from 'three';
import { makeRng } from '../core/Rng.js';
import { MASK } from '../world/Physics.js';
import { CELL } from './Particles.js';
import { DECAL } from './Decals.js';

/**
 * Material-specific impact reactions.
 *
 * This is where a shooter is won or lost. If every surface throws the same grey
 * puff, shooting feels like clicking on a texture; the moment concrete pulverises,
 * metal throws ricocheting sparks that skitter off the floor, wood splinters and
 * sand erupts in a slow plume, the world starts behaving like matter.
 *
 * Every recipe below is built from the same four ingredients — an expanding puff,
 * a directional burst, solid debris and a decal — but the *distribution* of energy
 * between them is what encodes the material:
 *
 *   concrete  brittle, powdery      -> lots of fine dust, few hard chips
 *   metal     elastic, incandescent -> almost no dust, a shower of hot ricochets
 *   wood      fibrous               -> long splinters, little dust, no bounce
 *   dirt/sand granular              -> a large slow plume, heavy clods, no rebound
 *   glass     brittle, specular     -> shards that catch light, a hard flash
 *   flesh     wet                   -> a directional cone plus a hanging mist
 *   water     fluid                 -> a crown, a ring, and nothing left behind
 *
 * All numbers are metres and seconds. Colours are linear-light, not sRGB.
 */

export const TUNING = {
  /* how much of the incoming energy comes back off the surface */
  restitution: { concrete: 0.28, metal: 0.52, wood: 0.16, dirt: 0.08, sand: 0.06, glass: 0.42, flesh: 0.05, water: 0.1 },
  decalSize: { concrete: 0.24, metal: 0.16, wood: 0.22, dirt: 0.32, sand: 0.36, glass: 0.5, flesh: 0.4, water: 0.5 },
  dustLinger: 2.6, // seconds the fine suspended dust hangs around
  maxSparks: 30,

  /* ---------------------------------------------------------------------- *
   * Dynamic light budget — all values in candela.
   *
   * `renderer.useLegacyLights` is off in r169, so a PointLight is a physical
   * emitter: it delivers `I / d²` lux at d metres, windowed to zero at
   * `distance`. The only other light in the scene is the sun, a DirectionalLight
   * at intensity 7.6 — that is 7.6 lux on every face-on surface in the level,
   * everywhere, with no falloff at all.
   *
   * So the useful way to read one of these numbers is **"how many suns at one
   * metre"**, and the radius at which it stops mattering is where I/d² drops
   * below the ambient fill (~0.5 lx):
   *
   *     I = 130  ->  14.4 lx at 3 m (2 suns), 1.3 lx at 10 m, 0.24 lx at 23 m
   *     I = 900  ->  4.6 lx at *fourteen* metres — 60% of full daylight on
   *                  every surface in the street, which is why the road under
   *                  the camera changed hue rather than just brightening.
   *
   * `distance` is a clip, not a falloff: at 34 m the window function is still
   * 0.94 at 14 m, so it was doing nothing. It is now set just past the radius
   * where the inverse square has already died, which is what makes it free.
   *
   * These lights do not cast shadows, and at these intensities that is finally
   * defensible. A PointLight shadow is a cube map — six full renders of the
   * level for a flash that lasts 50 ms — which is not affordable at any
   * quality level. The leak is instead bounded geometrically: the muzzle light
   * reaches 7 m, so the most it can do through a wall is put a faint warm wash
   * on whatever is stood right behind it, rather than lighting the far side of
   * a building.
   * ---------------------------------------------------------------------- */
  explosionLight: 130,
  explosionRange: 15,
  explosionLightLife: 0.4,
  metalLight: 7,
  metalRange: 3.2,
  glassLight: 4.5,
  glassRange: 2.6,
};

/* ---------------------------------------------------------------------- *
 * Zero-allocation vector scratch. Impacts fire dozens of times a second in
 * bursts; nothing here may touch the heap.
 * ---------------------------------------------------------------------- */

let T1x = 0, T1y = 0, T1z = 0, T2x = 0, T2y = 0, T2z = 0;
let Dx = 0, Dy = 0, Dz = 0;
let Rx = 0, Ry = 0, Rz = 0;

/** Orthonormal basis around an axis. */
function basis(nx, ny, nz) {
  let ax = 0, ay = 0, az = 0;
  if (Math.abs(ny) < 0.9) ay = 1; else ax = 1;
  T1x = ay * nz - az * ny;
  T1y = az * nx - ax * nz;
  T1z = ax * ny - ay * nx;
  const l = Math.hypot(T1x, T1y, T1z) || 1;
  T1x /= l; T1y /= l; T1z /= l;
  T2x = ny * T1z - nz * T1y;
  T2y = nz * T1x - nx * T1z;
  T2z = nx * T1y - ny * T1x;
}

/** Random direction inside a cone around (dx,dy,dz). `amount` ~ tan(half-angle). */
function scatter(dx, dy, dz, amount, rng) {
  basis(dx, dy, dz);
  const u = (rng() * 2 - 1) * amount;
  const v = (rng() * 2 - 1) * amount;
  Dx = dx + T1x * u + T2x * v;
  Dy = dy + T1y * u + T2y * v;
  Dz = dz + T1z * u + T2z * v;
  const l = Math.hypot(Dx, Dy, Dz) || 1;
  Dx /= l; Dy /= l; Dz /= l;
}

function reflect(dx, dy, dz, nx, ny, nz) {
  const d = dx * nx + dy * ny + dz * nz;
  Rx = dx - 2 * d * nx;
  Ry = dy - 2 * d * ny;
  Rz = dz - 2 * d * nz;
}

const _o = new THREE.Vector3();
const _d = new THREE.Vector3();

/** Surface -> sound bank registered by AudioEngine. */
const AUDIO_FOR = {
  concrete: 'impact_concrete',
  metal: 'impact_metal',
  wood: 'impact_wood',
  dirt: 'impact_dirt',
  sand: 'impact_dirt',
  glass: 'impact_glass',
  flesh: 'impact_flesh',
  water: 'impact_water',
};

/* ========================================================================== */

export class Impacts {
  constructor(ctx, particles, decals, lights) {
    this.ctx = ctx;
    this.p = particles;
    this.decals = decals;
    this.lights = lights;
    // A private stream, so FX never perturbs the gameplay RNG's determinism.
    this.rng = makeRng(0x51fed00d);
  }

  /**
   * A real Vector3, not a plain {x,y,z}: the audio engine is entitled to call
   * `.clone()` or `.distanceTo()` on what it is handed.
   */
  _audio(id, px, py, pz, volume = 1) {
    this.ctx.events.emit('audio:play', { id, position: new THREE.Vector3(px, py, pz), volume });
  }

  /**
   * Map a surface to a sound the AudioEngine actually has. Sand shares dirt's
   * bank and a headshot is still a flesh impact — emitting an id nobody registered
   * is a silent bullet, which is worse than a slightly generic one.
   */
  _impactAudio(material, px, py, pz) {
    const id = AUDIO_FOR[material] ?? 'impact_concrete';
    this._audio(id, px, py, pz);
  }

  /**
   * The entry point for `hit:surface`.
   * @param {number} dx,dy,dz incoming bullet direction (unit). Sparks and spray
   *   follow the reflection of this vector; without it, every impact fans out
   *   symmetrically and the geometry of the shot is lost.
   */
  surface(px, py, pz, nx, ny, nz, material, dx, dy, dz) {
    const rng = this.rng;
    const l = Math.hypot(nx, ny, nz) || 1;
    nx /= l; ny /= l; nz /= l;
    if (dx === undefined || (dx === 0 && dy === 0 && dz === 0)) {
      dx = -nx; dy = -ny; dz = -nz;
    }
    reflect(dx, dy, dz, nx, ny, nz);
    // Blend the mirror reflection back towards the surface normal: real spall
    // sprays between the two, not purely along either.
    Rx = Rx * 0.55 + nx * 0.6;
    Ry = Ry * 0.55 + ny * 0.6;
    Rz = Rz * 0.55 + nz * 0.6;
    const rl = Math.hypot(Rx, Ry, Rz) || 1;
    Rx /= rl; Ry /= rl; Rz /= rl;

    switch (material) {
      case 'metal': this._metal(px, py, pz, nx, ny, nz, rng); break;
      case 'wood': this._wood(px, py, pz, nx, ny, nz, rng); break;
      // The airborne colour, not the soil colour: dust in sunlight reads far
      // lighter than the ground it was thrown out of.
      case 'dirt': this._granular(px, py, pz, nx, ny, nz, rng, 0.42, 0.33, 0.22); break;
      case 'sand': this._granular(px, py, pz, nx, ny, nz, rng, 0.64, 0.545, 0.35); break;
      case 'glass': this._glass(px, py, pz, nx, ny, nz, rng); break;
      case 'flesh': this._flesh(px, py, pz, dx, dy, dz, rng, false); break;
      case 'water': this._water(px, py, pz, nx, ny, nz, rng); break;
      case 'concrete':
      default: this._concrete(px, py, pz, nx, ny, nz, rng); break;
    }

    if (material !== 'water' && material !== 'flesh') this._decal(px, py, pz, nx, ny, nz, material, rng);
    this._impactAudio(material, px, py, pz);
    // Metal gets a second, separate whine — the round leaving, not the round landing.
    if (material === 'metal') this._audio('ricochet', px, py, pz, 0.8);
  }

  _decal(px, py, pz, nx, ny, nz, material, rng) {
    const size = (TUNING.decalSize[material] ?? 0.14) * (0.8 + rng() * 0.5);
    switch (material) {
      case 'metal':
        this.decals.spawn(px, py, pz, nx, ny, nz, DECAL.HOLE_METAL, size, 1, 1, 1, 90, 4, 0, 1, 0.25);
        break;
      case 'wood':
        this.decals.spawn(px, py, pz, nx, ny, nz, DECAL.HOLE_WOOD, size, 1, 0.95, 0.9, 90, 4, 0, 1, 0);
        break;
      case 'glass':
        this.decals.spawn(px, py, pz, nx, ny, nz, DECAL.GLASS_CRACK, size, 1, 1, 1, 90, 4, 0.25, 0.35, 0.5);
        break;
      case 'dirt':
        this.decals.spawn(px, py, pz, nx, ny, nz, DECAL.SCORCH, size, 0.55, 0.42, 0.30, 40, 6, 0, 1, 0);
        break;
      case 'sand':
        this.decals.spawn(px, py, pz, nx, ny, nz, DECAL.SCORCH, size, 0.95, 0.82, 0.58, 30, 6, 0, 1, 0);
        break;
      default:
        this.decals.spawn(px, py, pz, nx, ny, nz, DECAL.HOLE_CONCRETE, size, 1, 1, 1, 90, 4, 0, 1, 0);
    }
  }

  /* ------------------------------- concrete ------------------------------- */

  /**
   * Brittle mineral: the round pulverises a cone of material into powder and
   * knocks a few solid chips loose. The signature is the *dust*, and specifically
   * that some of it hangs in the air long after the puff has gone.
   */
  _concrete(px, py, pz, nx, ny, nz, rng) {
    const p = this.p;

    // The puff is built in three layers, because a single ring of identical sprites
    // flying apart at the same speed always reads as a handful of cotton balls
    // rather than one cloud. Each layer is smaller and more numerous than the last
    // version of this effect: nine overlapping 40 cm puffs make a volume, five
    // separated 95 cm puffs make five discs.
    //
    // Layer one is the *body*: slow, heavily overlapped, and it stays put. It is
    // what the eye registers as a volume of pulverised material.
    //
    // Pulverised concrete in daylight is *pale*, not white. The old base colour of
    // 1.02 was multiplied by the shader's own lighting (up to ~1.8x with the sun on
    // it) and landed near 1.9 in linear light — brighter than a sunlit plaster wall,
    // which sits nearer 1.3. Dust is never the brightest thing in a scene lit by
    // the sun; it is lit *by* that sun and it scatters, so it has to land below it.
    for (let i = 0; i < 9; i++) {
      scatter(nx, ny, nz, 0.7, rng);
      const sp = 0.3 + rng() * 1.1;
      p.beginSoft()
        .pos(px + Dx * (0.06 + rng() * 0.18), py + Dy * (0.06 + rng() * 0.18), pz + Dz * (0.06 + rng() * 0.18))
        .vel(Dx * sp, Dy * sp + 0.30, Dz * sp)
        .life(0.75 + rng() * 0.8)
        .drag(4.0)
        .grav(0.05)
        .size(0.12 + rng() * 0.08, 0.34 + rng() * 0.36)
        .roll(rng() * 6.28, (rng() - 0.5) * 2.2)
        .colA(0.60, 0.58, 0.545, 0.42 + rng() * 0.34)
        .colB(0.27, 0.26, 0.245)
        .atlas(rng() < 0.5 ? CELL.SMOKE : CELL.DUST)
        .shade(1)
        .fade(0.03, 1.5)
        .go();
    }

    // Layer two is the *ejecta*: small, fast, thrown clear of the body. These are
    // what give the puff a ragged, energetic silhouette.
    for (let i = 0; i < 7; i++) {
      scatter(nx, ny, nz, 1.05, rng);
      const sp = 1.8 + rng() * 3.4;
      p.beginSoft()
        .pos(px + Dx * 0.08, py + Dy * 0.08, pz + Dz * 0.08)
        .vel(Dx * sp, Dy * sp + 0.4, Dz * sp)
        .life(0.4 + rng() * 0.45)
        .drag(6.5)
        .grav(0.06)
        .size(0.04, 0.14 + rng() * 0.16)
        .roll(rng() * 6.28, (rng() - 0.5) * 4.5)
        .colA(0.56, 0.545, 0.515, 0.30 + rng() * 0.28)
        .colB(0.26, 0.255, 0.24)
        .atlas(CELL.DUST)
        .shade(1)
        .fade(0.03, 1.6)
        .go();
    }

    // Fine suspended dust: slow, faint, long-lived. This is what makes a wall look
    // like it is still recovering from being shot.
    for (let i = 0; i < 4; i++) {
      scatter(nx, ny, nz, 1.3, rng);
      p.beginSoft()
        .pos(px + Dx * 0.3, py + Dy * 0.3, pz + Dz * 0.3)
        .vel(Dx * 0.5, Dy * 0.5 + 0.22, Dz * 0.5)
        .life(TUNING.dustLinger * (0.7 + rng() * 0.6))
        .drag(1.5)
        .grav(0.015)
        .size(0.22 + rng() * 0.14, 0.7 + rng() * 0.6)
        .roll(rng() * 6.28, (rng() - 0.5) * 0.5)
        .colA(0.50, 0.49, 0.465, 0.10 + rng() * 0.08)
        .colB(0.30, 0.295, 0.285)
        .atlas(CELL.DUST)
        .shade(1)
        .fade(0.25, 1.3)
        .go();
    }

    // Grit: sharp little specks flung out along the reflection.
    for (let i = 0; i < 10; i++) {
      scatter(Rx, Ry, Rz, 0.75, rng);
      const sp = 3 + rng() * 7;
      p.beginAlpha()
        .pos(px, py, pz)
        .vel(Dx * sp, Dy * sp, Dz * sp)
        .life(0.35 + rng() * 0.4)
        .drag(1.2)
        .grav(1)
        .size(0.008 + rng() * 0.01)
        .roll(rng() * 6.28, (rng() - 0.5) * 20)
        .col(0.45, 0.43, 0.40, 0.9)
        .atlas(CELL.FLECK)
        .fade(0.02, 0.7)
        .go();
    }

    // Solid chips with real physics. Deliberately darker than the dust they came
    // out of — aggregate broken out of a wall is the wet grey of the core, not the
    // pale colour of the pulverised surface, and it needs to read against a bright
    // wall rather than vanish into it.
    const chips = 5 + (rng() * 4) | 0;
    for (let i = 0; i < chips; i++) {
      scatter(Rx, Ry, Rz, 0.7, rng);
      const sp = 2.5 + rng() * 6;
      const s = 0.014 + rng() * 0.024;
      const g = 0.16 + rng() * 0.13;
      p.chips.spawn(px + Dx * 0.02, py + Dy * 0.02, pz + Dz * 0.02, Dx * sp, Dy * sp + 1, Dz * sp,
        s, s * (0.5 + rng()), s * (0.5 + rng()), 1.6 + rng() * 1.4, g, g * 0.98, g * 0.94, 0.35, TUNING.restitution.concrete);
    }

    // A brief spark of the bullet's own energy — dim, warm, one frame or two.
    this._flashPop(px + nx * 0.02, py + ny * 0.02, pz + nz * 0.02, 0.9, 0.42, 0.14, 0.28, 0.05);
  }

  /* -------------------------------- metal --------------------------------- */

  /**
   * Elastic and conductive: almost nothing pulverises, so instead of dust you get
   * incandescent fragments of jacket and steel that bounce. The colour ramp from
   * white-hot to cherry red over a third of a second is the whole read.
   */
  _metal(px, py, pz, nx, ny, nz, rng) {
    const p = this.p;

    // Hard flash right at the point of contact. The colour values are far above 1
    // on purpose: this is an incandescent event and it has to survive tone mapping
    // and blow out into the bloom pass, not sit at display white.
    this._flashPop(px + nx * 0.02, py + ny * 0.02, pz + nz * 0.02, 9.0, 5.2, 1.8, 0.32, 0.06);
    this.lights.pulse(px + nx * 0.15, py + ny * 0.15, pz + nz * 0.15, 0xffc070,
      TUNING.metalLight, TUNING.metalRange, 0.08);

    // Spark shower. Four things matter here and all of them are easy to get wrong:
    //
    //  - Speed is distributed as rng², so most sparks stay near the impact and a
    //    few outrun the rest. A uniform distribution gives every spark the same
    //    trip and the shower reads as a single expanding shell.
    //  - **Length must follow speed.** `stretch` is multiplied by the particle's
    //    *current* speed in the vertex shader, so a fast spark is a long streak
    //    and the same spark is a short dash once drag has eaten its velocity. A
    //    field of equal-length lines is the tell of a vector-art starburst.
    //  - **They have to cool, and they have to dim.** `fadeOut` was 0.55, i.e. a
    //    *sub*-linear ramp: alpha was still 0.7 at the halfway point and 0.4 at
    //    80% of life, so every spark held full brightness for its whole visible
    //    trip and then vanished. Combined with an end colour of (1.8, 0.14, 0.02)
    //    — still three times over display white — nothing ever looked like it was
    //    losing heat. The end colour is now genuinely dim red, below 1, so the
    //    tail of the arc falls out of the bloom pass and reads as cooling metal.
    //  - **Gravity has to win.** Drag of 1.4 gives a terminal velocity of 7 m/s;
    //    raising gravity and dropping drag lets the parabola show inside the
    //    third of a second the spark is alive.
    const n = Math.min(TUNING.maxSparks, 22 + ((rng() * 8) | 0));
    for (let i = 0; i < n; i++) {
      scatter(Rx, Ry, Rz, 0.9, rng);
      const k = rng();
      const sp = 1.6 + k * k * 24;
      p.beginAdd()
        .pos(px + nx * 0.01, py + ny * 0.01, pz + nz * 0.01)
        .vel(Dx * sp, Dy * sp + 1.2, Dz * sp)
        // Fast sparks live longer, so the ones that travel furthest are also the
        // ones still visible when they start to fall.
        .life(0.24 + k * 0.55 + rng() * 0.2)
        .drag(0.85)
        .grav(1.7)
        .size(0.005 + rng() * 0.004)
        .stretch(1.4 + rng() * 1.6)
        .colA(6.0, 2.5, 0.55, 1.0)
        .colB(0.55, 0.055, 0.008)
        .atlas(CELL.SPARK)
        .fade(0.008, 1.9)
        .go();
    }

    // A dense, very short-lived knot of sparks right at the contact point, so the
    // shower has a hot origin instead of being uniformly sparse.
    for (let i = 0; i < 9; i++) {
      scatter(Rx, Ry, Rz, 1.2, rng);
      const sp = 0.8 + rng() * 3;
      p.beginAdd()
        .pos(px + nx * 0.01, py + ny * 0.01, pz + nz * 0.01)
        .vel(Dx * sp, Dy * sp, Dz * sp)
        .life(0.06 + rng() * 0.09)
        .drag(6)
        .grav(0.6)
        .size(0.009 + rng() * 0.008)
        .stretch(0.9)
        .colA(10.0, 5.6, 1.8, 1.0)
        .colB(1.2, 0.18, 0.02)
        .atlas(CELL.SPARK)
        .fade(0.01, 1.4)
        .go();
    }

    // A handful of the sparks are solid enough to bounce off the floor. These are
    // the ones the eye follows across the room: real geometry, integrated against
    // the collision world, so they skitter and rebound instead of passing through
    // it. Low drag and a long life so a bounce actually happens inside the
    // lifetime, and an end colour under 1.0 so a settling ember is a dull red coal
    // rather than a white dot lying on the floor.
    const ric = 6 + ((rng() * 4) | 0);
    for (let i = 0; i < ric; i++) {
      scatter(Rx, Ry, Rz, 0.7, rng);
      const sp = 6 + rng() * 13;
      // 2 mm across. These are additive and unlit, so their apparent brightness is
      // area × colour: a spark drawn 4 mm wide at arm's length is a neon tube, not
      // a fragment of jacket. Thin and merely hot beats fat and incandescent.
      p.embers.spawn(px + nx * 0.02, py + ny * 0.02, pz + nz * 0.02, Dx * sp, Dy * sp + 1.5, Dz * sp,
        0.0022, 0.042, 0.0022, 0.85 + rng() * 0.75,
        3.4, 1.45, 0.35, 0.22, 0.45, 0.5, 0.045, 0.004);
    }

    // Two small spall fragments with a metallic sheen.
    for (let i = 0; i < 2; i++) {
      scatter(Rx, Ry, Rz, 0.8, rng);
      const sp = 3 + rng() * 5;
      const s = 0.006 + rng() * 0.009;
      p.shards.spawn(px, py, pz, Dx * sp, Dy * sp + 1, Dz * sp, s, s, s * 0.4, 1.5 + rng(),
        0.55, 0.55, 0.58, 0.3, TUNING.restitution.metal);
    }

    // A wisp of vaporised jacket, dark and quick.
    for (let i = 0; i < 2; i++) {
      scatter(nx, ny, nz, 0.7, rng);
      p.beginSoft()
        .pos(px + Dx * 0.04, py + Dy * 0.04, pz + Dz * 0.04)
        .vel(Dx * 1.1, Dy * 1.1 + 0.6, Dz * 1.1)
        .life(0.55 + rng() * 0.4)
        .drag(3.2)
        .grav(-0.05)
        .size(0.03, 0.19 + rng() * 0.12)
        .roll(rng() * 6.28, (rng() - 0.5) * 2)
        .colA(0.30, 0.29, 0.30, 0.34)
        .colB(0.17, 0.17, 0.18)
        .atlas(CELL.SMOKE)
        .shade(1)
        .fade(0.08, 1.6)
        .go();
    }
  }

  /* --------------------------------- wood --------------------------------- */

  /** Fibrous: it tears rather than shatters, so splinters are long and land dead. */
  _wood(px, py, pz, nx, ny, nz, rng) {
    const p = this.p;

    // Wood barely produces dust — what it produces is *sawdust*, a small, pale,
    // short-lived cloud right at the hole. Keeping it tight and low-volume is what
    // stops wood reading as another kind of concrete.
    for (let i = 0; i < 8; i++) {
      scatter(nx, ny, nz, 0.85, rng);
      const sp = 1.2 + rng() * 2.6;
      p.beginSoft()
        .pos(px + Dx * 0.03, py + Dy * 0.03, pz + Dz * 0.03)
        .vel(Dx * sp, Dy * sp + 0.3, Dz * sp)
        .life(0.5 + rng() * 0.6)
        .drag(4.5)
        .grav(0.10)
        .size(0.03, 0.20 + rng() * 0.20)
        .roll(rng() * 6.28, (rng() - 0.5) * 2.6)
        .colA(0.42, 0.31, 0.175, 0.30 + rng() * 0.30)
        .colB(0.20, 0.148, 0.088)
        .atlas(CELL.DUST)
        .shade(1)
        .fade(0.07, 1.5)
        .go();
    }

    // Splinters: long, thin, tumbling, and they do not bounce. This is the whole
    // read for wood, so there are a lot of them and they are properly long —
    // aspect ratios of eight or ten to one, not chips with a slight stretch.
    const n = 12 + ((rng() * 6) | 0);
    for (let i = 0; i < n; i++) {
      scatter(Rx, Ry, Rz, 0.8, rng);
      const sp = 2.5 + rng() * 7;
      const len = 0.05 + rng() * 0.11;
      const th = 0.005 + rng() * 0.006;
      p.chips.spawn(px, py, pz, Dx * sp, Dy * sp + 1.2, Dz * sp,
        th, len, th * (0.7 + rng()), 1.6 + rng() * 1.4,
        0.34, 0.22, 0.11, 0.55, TUNING.restitution.wood);
    }

    // Two larger chunks knocked clean out of the board.
    for (let i = 0; i < 2; i++) {
      scatter(Rx, Ry, Rz, 0.5, rng);
      const sp = 1.8 + rng() * 3;
      const s = 0.016 + rng() * 0.018;
      p.chips.spawn(px, py, pz, Dx * sp, Dy * sp + 1, Dz * sp, s, s * 0.6, s * 0.8, 2.2 + rng(),
        0.28, 0.18, 0.09, 0.5, TUNING.restitution.wood);
    }

    for (let i = 0; i < 6; i++) {
      scatter(Rx, Ry, Rz, 0.9, rng);
      const sp = 2 + rng() * 5;
      p.beginAlpha()
        .pos(px, py, pz)
        .vel(Dx * sp, Dy * sp, Dz * sp)
        .life(0.4 + rng() * 0.3)
        .drag(1.6)
        .grav(1)
        .size(0.007 + rng() * 0.008)
        .roll(rng() * 6.28, (rng() - 0.5) * 18)
        .col(0.32, 0.22, 0.12, 0.9)
        .atlas(CELL.FLECK)
        .fade(0.02, 0.8)
        .go();
    }
  }

  /* ----------------------------- dirt and sand ---------------------------- */

  /**
   * Granular material absorbs almost everything and gives back a big, slow,
   * soft-edged plume — the opposite of concrete's sharp crack of dust — plus heavy
   * clods that land once and stay.
   */
  _granular(px, py, pz, nx, ny, nz, rng, r, g, b) {
    const p = this.p;

    // The plume body. Granular material absorbs the round rather than reflecting
    // it, so the dust boils outward slowly and stays together — high drag, low
    // launch speed. Throwing it fast is what turns a plume into confetti.
    //
    // The signature that separates dirt from concrete at a glance is *softness*:
    // twice as many sprites at half the speed with a wide spread of sizes and
    // opacities, so the plume has no countable elements and no hard silhouette.
    for (let i = 0; i < 14; i++) {
      scatter(nx, ny, nz, 0.85, rng);
      const sp = 0.4 + rng() * 1.4;
      p.beginSoft()
        .pos(px + Dx * (0.05 + rng() * 0.16), py + Dy * (0.05 + rng() * 0.16), pz + Dz * (0.05 + rng() * 0.16))
        .vel(Dx * sp, Dy * sp + 0.5, Dz * sp)
        .life(1.3 + rng() * 1.5)
        .drag(3.2)
        .grav(0.10)
        .size(0.1 + rng() * 0.09, 0.4 + rng() * 0.55)
        .roll(rng() * 6.28, (rng() - 0.5) * 1.8)
        .colA(r, g, b, 0.30 + rng() * 0.34)
        .colB(r * 0.55, g * 0.55, b * 0.58)
        // Alternate between the two billow sprites so neighbouring puffs in the
        // same plume never share a silhouette.
        .atlas(rng() < 0.5 ? CELL.DUST : CELL.SMOKE)
        .shade(1)
        .fade(0.06, 1.35)
        .go();
    }

    // The plume keeps rising and thinning long after the initial throw.
    for (let i = 0; i < 4; i++) {
      scatter(nx, ny, nz, 1.2, rng);
      p.beginSoft()
        .pos(px + Dx * 0.3, py + Dy * 0.3, pz + Dz * 0.3)
        .vel(Dx * 0.4, Dy * 0.4 + 0.45, Dz * 0.4)
        .life(2.2 + rng() * 1.4)
        .drag(1.1)
        .grav(0.02)
        .size(0.3, 1.0 + rng() * 0.8)
        .roll(rng() * 6.28, (rng() - 0.5) * 0.4)
        .colA(r * 0.95, g * 0.95, b, 0.11 + rng() * 0.12)
        .colB(r * 0.62, g * 0.62, b * 0.64)
        .atlas(CELL.DUST)
        .shade(1)
        .fade(0.3, 1.2)
        .go();
    }

    const clods = 5 + ((rng() * 4) | 0);
    for (let i = 0; i < clods; i++) {
      scatter(Rx, Ry, Rz, 0.85, rng);
      const sp = 1.5 + rng() * 4;
      const s = 0.010 + rng() * 0.022;
      p.chips.spawn(px, py, pz, Dx * sp, Dy * sp + 2, Dz * sp, s, s * 0.85, s, 1.8 + rng(),
        r * 0.8, g * 0.8, b * 0.8, 0.6, TUNING.restitution.dirt);
    }

    for (let i = 0; i < 12; i++) {
      scatter(nx, ny, nz, 1.1, rng);
      const sp = 2 + rng() * 6;
      p.beginAlpha()
        .pos(px, py, pz)
        .vel(Dx * sp, Dy * sp + 1, Dz * sp)
        .life(0.45 + rng() * 0.45)
        .drag(1.4)
        .grav(1)
        .size(0.006 + rng() * 0.009)
        .roll(rng() * 6.28, (rng() - 0.5) * 14)
        .col(r * 0.9, g * 0.9, b * 0.9, 0.85)
        .atlas(CELL.FLECK)
        .fade(0.02, 0.8)
        .go();
    }
  }

  /* --------------------------------- glass -------------------------------- */

  /** Brittle and specular: a hard bright flash, then shards that catch the sun. */
  _glass(px, py, pz, nx, ny, nz, rng) {
    const p = this.p;

    this._flashPop(px + nx * 0.02, py + ny * 0.02, pz + nz * 0.02, 2.2, 2.4, 2.8, 0.5, 0.06);
    // A small, cold pulse. Glass is the only surface whose flash is blue-white, and
    // one frame of the surrounding wall going cool is a strong material tell — but
    // it is a *reflection*, not a detonation, so it is a fraction of the metal
    // light and dies inside a 2.6 m radius.
    this.lights.pulse(px + nx * 0.1, py + ny * 0.1, pz + nz * 0.1, 0xbfd8ff,
      TUNING.glassLight, TUNING.glassRange, 0.06);

    // Shards: real geometry, low roughness, high bounce — they glitter as they
    // tumble because they are actually catching the scene lighting.
    const n = 8 + ((rng() * 6) | 0);
    for (let i = 0; i < n; i++) {
      scatter(Rx, Ry, Rz, 0.9, rng);
      const sp = 3 + rng() * 9;
      const s = 0.010 + rng() * 0.022;
      p.shards.spawn(px, py, pz, Dx * sp, Dy * sp + 1.5, Dz * sp, s, s * (0.6 + rng() * 0.8), s * 0.35,
        2.0 + rng() * 1.5, 0.80, 0.90, 1.0, 0.22, TUNING.restitution.glass);
    }

    // Airborne glitter: tiny additive sparkles with staggered lifetimes so the
    // cloud twinkles instead of fading uniformly.
    for (let i = 0; i < 14; i++) {
      scatter(Rx, Ry, Rz, 1.0, rng);
      const sp = 2.5 + rng() * 8;
      p.beginAdd()
        .pos(px, py, pz)
        .vel(Dx * sp, Dy * sp + 1, Dz * sp)
        .life(0.25 + rng() * 0.6)
        .drag(1.5)
        .grav(1)
        .size(0.006 + rng() * 0.008)
        .colA(1.6, 1.9, 2.2, 1.0)
        .colB(0.4, 0.55, 0.7)
        .atlas(CELL.GLOW)
        .fade(0.02, 1.1)
        .go();
    }

    // Powdered glass hangs like frost.
    for (let i = 0; i < 3; i++) {
      scatter(nx, ny, nz, 0.9, rng);
      p.beginSoft()
        .pos(px + Dx * 0.04, py + Dy * 0.04, pz + Dz * 0.04)
        .vel(Dx * 1.4, Dy * 1.4 + 0.3, Dz * 1.4)
        .life(0.7 + rng() * 0.5)
        .drag(3.6)
        .grav(0.05)
        .size(0.03, 0.24)
        .roll(rng() * 6.28, (rng() - 0.5) * 2)
        .colA(0.44, 0.48, 0.54, 0.16 + rng() * 0.16)
        .colB(0.27, 0.30, 0.34)
        .atlas(CELL.DUST)
        .shade(1)
        .fade(0.08, 1.6)
        .go();
    }
  }

  /* --------------------------------- flesh -------------------------------- */

  /**
   * A hit on a body: a dense cone of blood carried *through* along the bullet's
   * path, a fine mist that hangs where the round passed, and heavy droplets that
   * arc down. The spray follows the bullet, not the surface normal — spray coming
   * back at the shooter is the classic tell of a lazy blood effect.
   */
  _flesh(px, py, pz, dx, dy, dz, rng, headshot) {
    const p = this.p;
    const mult = headshot ? 1.8 : 1.0;

    const n = ((14 * mult) | 0);
    for (let i = 0; i < n; i++) {
      scatter(dx, dy, dz, 0.42, rng);
      const sp = (2.5 + rng() * 7) * mult;
      p.beginAlpha()
        .pos(px, py, pz)
        .vel(Dx * sp, Dy * sp + 0.6, Dz * sp)
        .life(0.4 + rng() * 0.5)
        .drag(2.2)
        .grav(1.2)
        .size(0.014 + rng() * 0.022, 0.02 + rng() * 0.03)
        .roll(rng() * 6.28, (rng() - 0.5) * 8)
        .colA(0.24, 0.012, 0.012, 0.95)
        .colB(0.10, 0.006, 0.006)
        .atlas(CELL.BLOOD)
        .fade(0.02, 0.9)
        .go();
    }

    // Aerosolised mist: this is what makes a hit read as wet.
    for (let i = 0; i < (8 * mult) | 0; i++) {
      scatter(dx, dy, dz, 0.7, rng);
      const sp = 1.2 + rng() * 3;
      p.beginSoft()
        .pos(px + Dx * 0.05, py + Dy * 0.05, pz + Dz * 0.05)
        .vel(Dx * sp, Dy * sp + 0.2, Dz * sp)
        .life(0.5 + rng() * 0.5)
        .drag(4.5)
        .grav(0.35)
        .size(0.04, 0.24 + rng() * 0.2)
        .roll(rng() * 6.28, (rng() - 0.5) * 2)
        .colA(0.30, 0.03, 0.03, 0.34)
        .colB(0.12, 0.012, 0.012)
        .atlas(CELL.DUST)
        .shade(1)
        .fade(0.06, 1.4)
        .go();
    }

    // Heavy droplets thrown sideways.
    for (let i = 0; i < (5 * mult) | 0; i++) {
      scatter(dx, dy, dz, 1.1, rng);
      const sp = 1.5 + rng() * 4;
      p.beginAlpha()
        .pos(px, py, pz)
        .vel(Dx * sp, Dy * sp + 2, Dz * sp)
        .life(0.7 + rng() * 0.5)
        .drag(0.6)
        .grav(1.6)
        .size(0.010 + rng() * 0.012)
        .roll(rng() * 6.28, (rng() - 0.5) * 6)
        .col(0.20, 0.010, 0.010, 0.95)
        .atlas(CELL.BLOOD)
        .fade(0.02, 0.6)
        .go();
    }

    // Splatter on whatever is behind the target.
    this._sprayDecal(px, py, pz, dx, dy, dz, 3.2 * mult, 0.28 * mult, rng);
    // …and a few drops on the floor beneath the wound.
    this._sprayDecal(px, py, pz, dx * 0.3, -1, dz * 0.3, 2.6, 0.22, rng);
  }

  /** Raycasts for a backing surface and puts a blood splat on it. */
  _sprayDecal(px, py, pz, dx, dy, dz, maxDist, size, rng) {
    if (!this.ctx.physics) return;
    const l = Math.hypot(dx, dy, dz) || 1;
    _o.set(px, py, pz);
    _d.set(dx / l, dy / l, dz / l);
    const hit = this.ctx.physics.raycast(_o, _d, maxDist, MASK.WORLD | MASK.PROP);
    if (!hit || !hit.hit) return;
    this.decals.spawn(
      hit.point.x, hit.point.y, hit.point.z,
      hit.normal.x, hit.normal.y, hit.normal.z,
      DECAL.BLOOD_SPLAT, size * (0.7 + rng() * 0.7),
      1, 1, 1, 75, 8, 0.12, 0.55, 0.75
    );
  }

  /* --------------------------------- water -------------------------------- */

  _water(px, py, pz, nx, ny, nz, rng) {
    const p = this.p;

    // Crown: a ring of sheet-like wisps thrown up and outward.
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2 + rng() * 0.3;
      const ox = Math.cos(a);
      const oz = Math.sin(a);
      const sp = 1.6 + rng() * 2.4;
      p.beginAlpha()
        .pos(px + ox * 0.03, py + 0.01, pz + oz * 0.03)
        .vel(ox * sp * 0.55, 3.2 + rng() * 2.4, oz * sp * 0.55)
        .life(0.45 + rng() * 0.3)
        .drag(1.1)
        .grav(1.5)
        .size(0.035, 0.10 + rng() * 0.06)
        .roll(rng() * 6.28, (rng() - 0.5) * 3)
        .colA(0.70, 0.80, 0.88, 0.55)
        .colB(0.45, 0.55, 0.65)
        .atlas(CELL.WISP)
        .fade(0.04, 1.1)
        .go();
    }

    // Droplets that arc and fall back.
    for (let i = 0; i < 14; i++) {
      scatter(0, 1, 0, 0.7, rng);
      const sp = 2 + rng() * 5;
      p.beginAlpha()
        .pos(px, py, pz)
        .vel(Dx * sp, Dy * sp, Dz * sp)
        .life(0.6 + rng() * 0.5)
        .drag(0.5)
        .grav(1.6)
        .size(0.006 + rng() * 0.01)
        .col(0.62, 0.74, 0.84, 0.8)
        .atlas(CELL.BLOOD)
        .fade(0.02, 0.7)
        .go();
    }

    // Mist above the impact.
    for (let i = 0; i < 3; i++) {
      p.beginSoft()
        .pos(px + (rng() - 0.5) * 0.1, py + 0.05, pz + (rng() - 0.5) * 0.1)
        .vel((rng() - 0.5) * 0.4, 0.7 + rng() * 0.4, (rng() - 0.5) * 0.4)
        .life(0.7 + rng() * 0.5)
        .drag(3.0)
        .grav(0.05)
        .size(0.05, 0.32)
        .roll(rng() * 6.28, (rng() - 0.5) * 1.5)
        .colA(0.46, 0.50, 0.55, 0.15 + rng() * 0.14)
        .colB(0.30, 0.34, 0.38)
        .atlas(CELL.SMOKE)
        .shade(1)
        .fade(0.1, 1.5)
        .go();
    }

    // Expanding ripple on the surface.
    this.decals.spawn(px, py, pz, nx, ny, nz, DECAL.RIPPLE, 0.9, 0.75, 0.85, 0.95, 1.6, 1.2, 1.4, 0.15, 0.9);
  }

  /* -------------------------------- helpers ------------------------------- */

  /** One-frame additive pop at the point of contact. */
  _flashPop(px, py, pz, r, g, b, size, life) {
    this.p.beginAdd()
      .pos(px, py, pz)
      .vel(0, 0, 0)
      .life(life)
      .drag(9)
      .grav(0)
      .size(size * 0.35, size)
      .roll(this.rng() * 6.28, 0)
      .colA(r, g, b, 1.0)
      .colB(r * 0.3, g * 0.25, b * 0.2)
      .atlas(CELL.GLOW)
      .fade(0.02, 2.0)
      .go();
  }

  /* ---------------------------- actor and death --------------------------- */

  actor(px, py, pz, dx, dy, dz, headshot) {
    this._flesh(px, py, pz, dx, dy, dz, this.rng, !!headshot);
    this._audio('impact_flesh', px, py, pz, headshot ? 1 : 0.85);
  }

  /**
   * A body hitting the ground: a dust puff kicked out from under it, and a pool
   * that spreads over the next few seconds. The pool is a single decal with a
   * growth curve, so it costs nothing to animate.
   */
  death(px, py, pz) {
    const rng = this.rng;
    const p = this.p;
    for (let i = 0; i < 5; i++) {
      const a = rng() * Math.PI * 2;
      const sp = 0.8 + rng() * 1.4;
      p.beginSoft()
        .pos(px + Math.cos(a) * 0.25, py + 0.08, pz + Math.sin(a) * 0.25)
        .vel(Math.cos(a) * sp, 0.35 + rng() * 0.3, Math.sin(a) * sp)
        .life(1.2 + rng() * 0.9)
        .drag(2.6)
        .grav(0.03)
        .size(0.12, 0.7 + rng() * 0.4)
        .roll(rng() * 6.28, (rng() - 0.5) * 1)
        .colA(0.42, 0.39, 0.34, 0.30)
        .colB(0.3, 0.28, 0.25)
        .atlas(CELL.DUST)
        .shade(1)
        .fade(0.15, 1.4)
        .go();
    }
    const ground = this.ctx.physics ? this.ctx.physics.groundHeight(px, pz, py + 0.5, 3) : py;
    this.decals.spawn(px, ground, pz, 0, 1, 0, DECAL.BLOOD_POOL, 1.5 + rng() * 0.7, 1, 1, 1,
      120, 10, 4.5, 0.18, 0.9);
    // A little arterial spatter around the body.
    for (let i = 0; i < 3; i++) {
      const a = rng() * Math.PI * 2;
      this.decals.spawn(px + Math.cos(a) * (0.3 + rng() * 0.7), ground, pz + Math.sin(a) * (0.3 + rng() * 0.7),
        0, 1, 0, DECAL.BLOOD_SPLAT, 0.3 + rng() * 0.4, 1, 1, 1, 90, 8, 0.4, 0.4, 0.7);
    }
  }

  /* ------------------------------- footsteps ------------------------------ */

  footstep(px, py, pz, material) {
    const rng = this.rng;
    let r = 0.4, g = 0.38, b = 0.35, a = 0.10, n = 2;
    if (material === 'dirt') { r = 0.30; g = 0.22; b = 0.14; a = 0.20; n = 3; }
    else if (material === 'sand') { r = 0.52; g = 0.44; b = 0.26; a = 0.26; n = 4; }
    else if (material === 'water') { r = 0.6; g = 0.7; b = 0.8; a = 0.2; n = 3; }
    else if (material === 'metal' || material === 'glass') return; // hard floors kick nothing up

    for (let i = 0; i < n; i++) {
      const ang = rng() * Math.PI * 2;
      const sp = 0.25 + rng() * 0.5;
      this.p.beginSoft()
        .pos(px + Math.cos(ang) * 0.08, py + 0.02, pz + Math.sin(ang) * 0.08)
        .vel(Math.cos(ang) * sp, 0.25 + rng() * 0.2, Math.sin(ang) * sp)
        .life(0.6 + rng() * 0.5)
        .drag(3.2)
        .grav(0.03)
        .size(0.05, 0.24 + rng() * 0.16)
        .roll(rng() * 6.28, (rng() - 0.5) * 1)
        .colA(r, g, b, a)
        .colB(r * 0.7, g * 0.7, b * 0.72)
        .atlas(CELL.DUST)
        .shade(1)
        .fade(0.2, 1.5)
        .go();
    }
  }

  /* ------------------------------- explosion ------------------------------ */

  /**
   * Fireball, then smoke, then the debris raining back down — in that order,
   * because that is the order a real detonation resolves and getting it wrong is
   * what makes an explosion look like a firework.
   */
  explosion(px, py, pz, power = 1) {
    const rng = this.rng;
    const p = this.p;
    const s = Math.cbrt(power);

    // Where the floor is. Everything that touches the ground — the dust skirt, the
    // scorch, the rolling smoke — is placed against this rather than against the
    // detonation point, so a charge that goes off half a metre up still produces a
    // skirt that lies *on* the road instead of hovering over it.
    const g = this.ctx.physics ? this.ctx.physics.groundHeight(px, pz, py + 1.5, 8) : py;
    const floor = Number.isFinite(g) ? g : py - 0.3;
    const near = py - floor < 2.2 * s; // close enough to the ground to interact with it

    this.lights.pulse(px, py + 0.5 * s, pz, 0xffb060,
      TUNING.explosionLight * power, TUNING.explosionRange * s, TUNING.explosionLightLife);

    // Core fireball. Twenty-two smaller sprites rather than fourteen large ones,
    // with lifetimes spread across a 3:1 range: burning gas has to *thin out* as it
    // dies, and when every sprite is the same size and expires at the same moment
    // the last frames of the fireball are a handful of separate peach discs — the
    // same countable-blob failure as the smoke, just with a warmer palette.
    for (let i = 0; i < 22; i++) {
      scatter(rng() * 2 - 1, rng() * 2 - 1, rng() * 2 - 1, 1, rng);
      const sp = (2 + rng() * 9) * s;
      p.beginAdd()
        .pos(px + Dx * 0.2 * s, py + Dy * 0.2 * s, pz + Dz * 0.2 * s)
        .vel(Dx * sp, Dy * sp + 1.5 * s, Dz * sp)
        .life(0.18 + rng() * 0.42)
        .drag(3.4)
        .grav(-0.3)
        .size(0.25 * s, (0.75 + rng() * 0.95) * s)
        .roll(rng() * 6.28, (rng() - 0.5) * 2)
        // Warm, not white. The green and blue channels are what a bloom pass turns
        // into a flashbulb: at (3.4, 1.7, 0.45) the tone mapper pulled the core to
        // near-white and the fireball lost its colour entirely. Holding green to a
        // third of red and blue to a fifteenth keeps it burning orange even where
        // it is blown out.
        .colA(3.2, 1.15, 0.22, 1.0)
        .colB(0.7, 0.12, 0.02)
        .atlas(CELL.SMOKE)
        .shade(0)
        .fade(0.02, 1.4)
        .go();
    }

    // Shockwave ring, flat on the ground. Smaller and dimmer than it was — a ring
    // that expands to seven metres in a quarter of a second and is bright enough to
    // read against daylight stops being a pressure front and becomes a cartoon
    // smoke ring drawn over the scene.
    p.beginAdd()
      .pos(px, py + 0.1, pz)
      .vel(0, 0, 0)
      .life(0.26)
      .drag(9)
      .grav(0)
      .size(0.4 * s, 4.5 * s)
      .roll(rng() * 6.28, 0)
      .colA(1.2, 0.78, 0.42, 0.55)
      .colB(0.35, 0.18, 0.10)
      .atlas(CELL.RING)
      .fade(0.02, 2.4)
      .go();

    /* ---- smoke, in three concentric shells ----
     *
     * A cloud is not a set of puffs of one size and one opacity flying apart at
     * one speed; that is what produced six countable discs. What the eye actually
     * reads as *volume* is a density gradient — an opaque, dark, slow core, a
     * translucent billowing body around it, and a thin, fast, nearly transparent
     * outer skin that catches the sun. So the same mass of smoke is spent on three
     * populations with different sizes, speeds, opacities and lifetimes, and each
     * particle jitters all four again.
     *
     * The other half of the fix is *value*. Smoke in daylight is mid-grey: it is
     * lit by the same sun as everything else and it scatters far more light than
     * it emits, so it can never be brighter than a sunlit surface next to it. The
     * shader multiplies the base colour by up to ~1.8 with the sun on it, so a
     * base of 0.26 was landing near 0.47 linear at the rim — against a plaster
     * wall at ~1.3 that should have been fine, but the *cores* were the same
     * value as the rims, so the cloud had no internal shading and the whole thing
     * flattened into one bright silhouette. The core is now a third the value of
     * the skin, which is where the sense of depth comes from.
     */

    // The governing constraint on all three shells is that **a sprite must always
    // be wider than the distance it has travelled from its neighbours**. Spread
    // velocity and sprite size are not independent knobs: a puff thrown at 3 m/s
    // is 2.4 m from where it started after 0.8 s, and if it is only 1 m across by
    // then, the cloud has holes in it and every element becomes countable. Speeds
    // here are therefore low and sizes large, and it is *drag* — not a slow launch
    // — that stops the cloud looking inert, because the deceleration is what the
    // eye reads as air resistance.

    // Shell 1 — core. Dark, dense, slow; it barely leaves the crater.
    for (let i = 0; i < 22; i++) {
      scatter(rng() * 2 - 1, rng() * 2 - 1, rng() * 2 - 1, 1, rng);
      const sp = (0.2 + rng() * 0.9) * s;
      p.beginSoft()
        .pos(px + Dx * 0.22 * s, py + Dy * 0.22 * s + 0.2, pz + Dz * 0.22 * s)
        .vel(Dx * sp, Dy * sp + 0.9 * s, Dz * sp)
        .life(1.8 + rng() * 2.2)
        .drag(2.4)
        .grav(-0.06)
        .size(0.34 * s, (0.85 + rng() * 0.5) * s)
        .roll(rng() * 6.28, (rng() - 0.5) * 1.4)
        .colA(0.115, 0.108, 0.101, 0.70 + rng() * 0.28)
        .colB(0.045, 0.043, 0.042)
        .atlas(rng() < 0.5 ? CELL.SMOKE : CELL.DUST)
        .shade(1)
        .fade(0.04, 1.2)
        .go();
    }

    // Shell 2 — body. The bulk of the cloud: mid-grey, overlapping, rising.
    for (let i = 0; i < 28; i++) {
      scatter(rng() * 2 - 1, rng() * 2 - 1, rng() * 2 - 1, 1, rng);
      const sp = (0.5 + rng() * 1.7) * s;
      p.beginSoft()
        .pos(px + Dx * 0.4 * s, py + Dy * 0.4 * s + 0.3, pz + Dz * 0.4 * s)
        .vel(Dx * sp, Dy * sp + 1.7 * s, Dz * sp)
        .life(2.4 + rng() * 2.6)
        .drag(1.9)
        .grav(-0.10)
        .size(0.5 * s, (1.35 + rng() * 0.9) * s)
        .roll(rng() * 6.28, (rng() - 0.5) * 1.0)
        .colA(0.175, 0.166, 0.156, 0.40 + rng() * 0.38)
        .colB(0.070, 0.067, 0.065)
        .atlas(rng() < 0.5 ? CELL.SMOKE : CELL.DUST)
        .shade(1)
        .fade(0.06, 1.15)
        .go();
    }

    // Shell 3 — skin. Big, thin, and the lightest of the three: this is the part
    // the sun actually reaches, and it is what gives the cloud a soft, torn
    // outline instead of a set of hard sprite edges.
    for (let i = 0; i < 16; i++) {
      scatter(rng() * 2 - 1, rng() * 2 - 1, rng() * 2 - 1, 1, rng);
      const sp = (1.0 + rng() * 2.2) * s;
      p.beginSoft()
        .pos(px + Dx * 0.6 * s, py + Dy * 0.6 * s + 0.45, pz + Dz * 0.6 * s)
        .vel(Dx * sp, Dy * sp + 2.2 * s, Dz * sp)
        .life(2.8 + rng() * 2.8)
        .drag(1.4)
        .grav(-0.13)
        .size(0.85 * s, (2.4 + rng() * 1.4) * s)
        .roll(rng() * 6.28, (rng() - 0.5) * 0.5)
        .colA(0.255, 0.243, 0.228, 0.14 + rng() * 0.18)
        .colB(0.105, 0.101, 0.098)
        .atlas(rng() < 0.5 ? CELL.DUST : CELL.SMOKE)
        .shade(1)
        .fade(0.10, 1.05)
        .go();
    }

    /* ---- ground interaction ----
     *
     * A detonation next to a road does two things to the road, and neither of them
     * was happening: it drives a fast, flat skirt of dust radially outward along
     * the surface, and it leaves a mark. The skirt is what fixes the cloud in
     * space — without it the smoke is an object floating near the ground rather
     * than something that happened *to* the ground.
     *
     * The skirt is deliberately much lighter than the smoke: it is pulverised road
     * and grit, not combustion product, and the contrast between the pale skirt and
     * the dark column is a large part of what makes a detonation legible.
     */
    if (near) {
      const skirtY = floor + 0.06;
      // Same rule as the column: the sprites have to stay wider than their
      // separation or the ring becomes a circle of countable discs on the road.
      // Twenty-four of them at 3 m/s under heavy drag settle into a continuous
      // annulus about three metres across.
      for (let i = 0; i < 24; i++) {
        const a = (i / 24) * Math.PI * 2 + rng() * 0.4;
        const sp = (2.4 + rng() * 3.0) * s;
        const r0 = 0.2 + rng() * 0.5;
        p.beginSoft()
          .pos(px + Math.cos(a) * r0, skirtY + rng() * 0.12, pz + Math.sin(a) * r0)
          // Almost no vertical component: a skirt hugs the floor and only lifts as
          // it slows. Throw it upward and it becomes a second smoke column.
          .vel(Math.cos(a) * sp, 0.3 + rng() * 0.45, Math.sin(a) * sp)
          .life(1.4 + rng() * 1.6)
          .drag(3.0)
          .grav(0.03)
          .size(0.4 * s, (1.5 + rng() * 0.9) * s)
          .roll(rng() * 6.28, (rng() - 0.5) * 0.9)
          .colA(0.40, 0.375, 0.335, 0.16 + rng() * 0.22)
          .colB(0.20, 0.192, 0.180)
          .atlas(CELL.DUST)
          .shade(1)
          .fade(0.08, 1.35)
          .go();
      }

      // A second, slower ring further out: the skirt does not stop at its own
      // radius, it keeps creeping and thinning after the fast front has passed.
      for (let i = 0; i < 10; i++) {
        const a = rng() * Math.PI * 2;
        const sp = (0.8 + rng() * 1.4) * s;
        p.beginSoft()
          .pos(px + Math.cos(a) * (0.9 + rng() * 1.1) * s, skirtY + rng() * 0.2, pz + Math.sin(a) * (0.9 + rng() * 1.1) * s)
          .vel(Math.cos(a) * sp, 0.45 + rng() * 0.35, Math.sin(a) * sp)
          .life(2.6 + rng() * 2.0)
          .drag(1.3)
          .grav(0.01)
          .size(0.8 * s, (2.6 + rng() * 1.4) * s)
          .roll(rng() * 6.28, (rng() - 0.5) * 0.35)
          .colA(0.36, 0.34, 0.305, 0.07 + rng() * 0.10)
          .colB(0.19, 0.183, 0.174)
          .atlas(CELL.DUST)
          .shade(1)
          .fade(0.18, 1.2)
          .go();
      }
    }

    // Burning fragments. Ballistic, cooling, and of varying length: speed is drawn
    // from a squared distribution so a few outrun the rest, `stretch` turns that
    // speed into streak length, and the end colour is under 1.0 so a spark at the
    // top of its arc has visibly gone from white to dull red.
    for (let i = 0; i < 26; i++) {
      scatter(rng() * 2 - 1, rng() * 2 - 1, rng() * 2 - 1, 1, rng);
      const k = rng();
      const sp = (4 + k * k * 22) * s;
      p.beginAdd()
        .pos(px, py + 0.2, pz)
        .vel(Dx * sp, Math.abs(Dy) * sp + 2, Dz * sp)
        .life(0.55 + k * 0.9 + rng() * 0.4)
        .drag(0.7)
        .grav(1.6)
        .size(0.008 + rng() * 0.008)
        .stretch(1.0 + rng() * 1.8)
        .colA(6.0, 2.6, 0.55, 1.0)
        .colB(0.5, 0.045, 0.006)
        .atlas(CELL.SPARK)
        .fade(0.01, 1.8)
        .go();
    }

    // A handful of the fragments are solid and land: they bounce off the road and
    // sit there glowing down through red, which is what gives the aftermath a floor.
    const coals = 8 + ((rng() * 5) | 0);
    for (let i = 0; i < coals; i++) {
      scatter(rng() * 2 - 1, rng() * 2 - 1, rng() * 2 - 1, 1, rng);
      const sp = (5 + rng() * 11) * s;
      p.embers.spawn(px, py + 0.2, pz, Dx * sp, Math.abs(Dy) * sp + 3, Dz * sp,
        0.0026, 0.048, 0.0026, 1.3 + rng() * 1.1,
        3.6, 1.5, 0.32, 0.2, 0.4, 0.42, 0.035, 0.003);
    }

    const chunks = 10 + ((rng() * 8) | 0);
    for (let i = 0; i < chunks; i++) {
      scatter(rng() * 2 - 1, rng() * 2 - 1, rng() * 2 - 1, 1, rng);
      const sp = (4 + rng() * 12) * s;
      const sz = (0.02 + rng() * 0.05) * s;
      p.chips.spawn(px, py + 0.2, pz, Dx * sp, Math.abs(Dy) * sp + 3, Dz * sp,
        sz, sz * 0.7, sz * 0.9, 2.5 + rng() * 1.5, 0.16, 0.14, 0.12, 0.25, 0.3);
    }

    // The mark. Two decals rather than one: a wide, faint soot wash for the area
    // the skirt swept, and a small, hard, near-black core for the seat of the
    // charge. A single mid-density disc reads as a texture someone stuck on the
    // road; the pair reads as burning.
    this.decals.spawn(px, floor, pz, 0, 1, 0, DECAL.SCORCH, 4.2 * s, 0.85, 0.83, 0.80,
      120, 10, 0.5, 0.35, 0);
    this.decals.spawn(px, floor, pz, 0, 1, 0, DECAL.SCORCH, 2.0 * s, 0.5, 0.48, 0.47,
      120, 10, 0.18, 0.5, 0.1);

    this._audio('explosion', px, py, pz, 1);
  }

  /* -------------------------------- casings ------------------------------- */

  shell(px, py, pz, vx, vy, vz) {
    const rng = this.rng;
    // Geometry is a unit-ish cylinder: this works out to a ~5 mm x 45 mm brass case.
    this.p.shells.spawn(px, py, pz, vx, vy, vz,
      0.011, 0.020, 0.011, 4.0 + rng(),
      0.62, 0.40, 0.11, 0.20, 0.34);
  }
}
